/* eslint-disable camelcase */
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

// Get environment-specific New Relic configuration
const getNewRelicConfig = () => {
  const { env } = window;

  if (!env) {
    console.warn('Environment configuration not loaded');
    return null;
  }

  return {
    accountID: env.NEW_RELIC_ACCOUNT_ID,
    trustKey: env.NEW_RELIC_TRUST_KEY,
    agentID: env.NEW_RELIC_AGENT_ID,
    licenseKey: env.NEW_RELIC_LICENSE_KEY,
    applicationID: env.NEW_RELIC_APPLICATION_ID,
  };
};

// Initialize New Relic
let newRelicAgent: BrowserAgent | null = null;

export const initializeNewRelic = (): BrowserAgent | null => {
  if (newRelicAgent) {
    return newRelicAgent;
  }

  const config = getNewRelicConfig();

  if (!config) {
    console.warn('New Relic configuration not available');
    return null;
  }

  const options = {
    init: {
      distributed_tracing: { enabled: true }, // Cross-service request tracing
      performance: {
        capture_measures: true, // Custom performance.measure() calls
        capture_marks: true, // Custom performance.mark() calls
        capture_detail: false, // Detailed browser performance metrics
      },
      browser_consent_mode: { enabled: false }, // GDPR/privacy consent handling
      privacy: { cookies_enabled: true }, // Allow New Relic cookies
      ajax: {
        deny_list: ['bam.nr-data.net'],
        capture_payloads: 'query', // Capture query parameters only
        capture_body: false, // Don't capture request/response bodies for privacy
      }, // AJAX monitoring config
      session_trace: { enabled: true }, // User session activity tracking
      session_replay: { enabled: false }, // Record user sessions (privacy concern)
      page_view_timing: { enabled: true }, // Page load performance metrics
      generic_events: { enabled: true }, // Custom events
      user_actions: { enabled: true }, // User interaction tracking
      metrics: { enabled: true }, // Custom metrics
      page_action: { enabled: true }, // Page-level events
      jserrors: { enabled: true }, // JavaScript error tracking
      logging: { enabled: false }, // Console log capture (dev only)
    },
    info: {
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey: config.licenseKey,
      applicationID: config.applicationID,
      sa: 1,
    },
    loader_config: {
      accountID: config.accountID,
      trustKey: config.trustKey,
      agentID: config.agentID,
      licenseKey: config.licenseKey,
      applicationID: config.applicationID,
    },
  };

  try {
    newRelicAgent = new BrowserAgent(options);
    console.log('New Relic initialized successfully');
    return newRelicAgent;
  } catch (error) {
    console.error('Failed to initialize New Relic:', error);
    return null;
  }
};

// Get the New Relic agent instance
export const getNewRelicAgent = () => newRelicAgent;

// Set user context and custom attributes that will be attached to all events
export const setUserContext = (
  userId: string,
  additionalAttributes?: Record<string, string | number | boolean>,
) => {
  if (!newRelicAgent) {
    console.warn('New Relic agent not initialized');
    return;
  }

  newRelicAgent.setUserId(userId);
  newRelicAgent.setCustomAttribute('userId', userId);

  if (additionalAttributes) {
    Object.entries(additionalAttributes).forEach(([key, value]) => {
      newRelicAgent?.setCustomAttribute(key, value);
    });
  }
};

// Add page action using the initialized agent
export const capturePageAction = (name: string, body: object) => {
  if (!newRelicAgent) {
    console.warn('New Relic agent not initialized');
    return;
  }
  newRelicAgent.addPageAction(name, body);
};
